import { z } from "zod";
import { InputBindingSchema, WorkflowBindingSchema } from "./analyticsColumn";
import {
  analyticsOutputRole,
  analyticsPopulationSource,
  analyticsRunState,
  invalidOutputCause,
  workflowStatus,
} from "./analyticsEnums";

/**
 * The wire shapes of the analytics workflow routes, mounted under
 * `/controls/v2/control/{controlId}/analytics` for a control and
 * `/work-steps/work-step/{workStepId}/analytics` for a work step. Both serve
 * the same shapes.
 *
 * These are the routes' own response projections, which are narrower than — and
 * named differently from — the persisted shapes in `analyticsRunManifest.ts` and
 * `analyticsColumnResult.ts`. Modelling them separately is what keeps a route
 * projection from being mistaken for the record behind it: the manifest read
 * returns `fileId`/`description` where the record carries `abFileId`/
 * `roleRationale`, and carries no `roleConfidence` at all.
 *
 * Fields the backend declares with a default are `.default(...)` here rather than
 * required, so a response that omits one parses to the same value the backend
 * would have sent.
 */

/** One offerable workflow, as the dropdown renders it. */
export const analyticsWorkflowOptionSchema = z.object({
  id: z.number(),
  name: z.string(),
  categoryId: z.number().nullable().default(null),
  publishedVersionId: z.number(),
  isAssociated: z.boolean(),
  // Null when the engine's payload carries no publication timestamp; the
  // dropdown then omits the date rather than falling back to an edit time.
  lastPublishedAt: z.string().nullable().default(null),
  // The promoted version's label (AuditBoard's `version`, e.g. "4"). Null when
  // the backend could not read it.
  activeVersion: z.string().nullable().default(null),
});

export type AnalyticsWorkflowOption = z.infer<
  typeof analyticsWorkflowOptionSchema
>;

/**
 * Associated workflows first, declaration order preserved within each group.
 *
 * `autoSelectedWorkflowId` is set only when there is exactly one association —
 * auto-selecting the first of several would be a decision the auditor did not
 * make.
 */
export const analyticsWorkflowListSchema = z.object({
  workflows: z.array(analyticsWorkflowOptionSchema).default([]),
  autoSelectedWorkflowId: z.number().nullable().default(null),
});

export type AnalyticsWorkflowList = z.infer<typeof analyticsWorkflowListSchema>;

export const analyticsCategoryOptionSchema = z.object({
  categoryId: z.number(),
  name: z.string(),
});

export type AnalyticsCategoryOption = z.infer<
  typeof analyticsCategoryOptionSchema
>;

/** Only categories that grant workflow-creation permission are listed. */
export const analyticsCategoryListSchema = z.object({
  categories: z.array(analyticsCategoryOptionSchema).default([]),
});

export type AnalyticsCategoryList = z.infer<typeof analyticsCategoryListSchema>;

/**
 * The freshly created workflow shell, plus the deep link the auditor builds it
 * behind.
 *
 * `clearedInputBindings` is read before the shell round trip, and the write is
 * conditioned on the payload those keys were read from: a binding write landing
 * inside that window answers 409 rather than being cleared unlisted, so the
 * list is exact whenever the request succeeds.
 */
export const analyticsConfigureResponseSchema = z.object({
  /**
   * Where to send the browser to author the workflow that was just created:
   * the Cascade canvas, reached through AuditBoard's own sign-in bounce.
   *
   * **Single use.** The URL carries a Cascade session credential in its query
   * string, minted for the click that produced this response. Navigate with it;
   * do not store it, and do not keep rendering it after the hand-off — the
   * `workflow-link` route re-mints one on demand.
   *
   * Nullable because a tenant that records no Cascade workbook for the workflow
   * has no canvas to open. The workflow was still created and the binding still
   * written, so this reports a missing link rather than a failed request.
   */
  deepLinkUrl: z.string().nullable().default(null),
  workflowBinding: WorkflowBindingSchema,
  clearedInputBindings: z.array(z.string()).default([]),
});

export type AnalyticsConfigureResponse = z.infer<
  typeof analyticsConfigureResponseSchema
>;

/**
 * Where to open the column's bound workflow in Optro Analytics, minted per
 * request.
 *
 * Fetched at the moment of the click rather than carried on the status payload,
 * because the URL holds a live Cascade session credential and the status is
 * polled — a polled credential sits in memory and in logs long after the click
 * it was minted for.
 *
 * `url` is null, with a 200, for every state where the data says there is
 * nothing to open: no binding, a workflow AuditBoard no longer holds, or a
 * workflow whose tenant records no Cascade workbook. Render no link; never an
 * empty `href`.
 */
export const analyticsWorkflowLinkSchema = z.object({
  url: z.string().nullable().default(null),
});

export type AnalyticsWorkflowLink = z.infer<typeof analyticsWorkflowLinkSchema>;

/** What attach answers with: the new binding and its status. */
export const analyticsBindingAndStatusSchema = z.object({
  workflowBinding: WorkflowBindingSchema,
  status: workflowStatus,
  // What re-pointing the column at another workflow discarded, reported rather
  // than dropped silently. Read before the round trip, as configure's field of
  // this name is; a concurrent write answers 409 rather than going unlisted,
  // so the list is exact whenever the request succeeds.
  clearedInputBindings: z.array(z.string()).default([]),
});

export type AnalyticsBindingAndStatus = z.infer<
  typeof analyticsBindingAndStatusSchema
>;

export const analyticsLastRunSchema = z.object({
  runId: z.string(),
  // `.catch("unknown")`: the enum's own unrecognised-status bucket, so a state
  // this build has not heard of degrades to it instead of failing the whole
  // status parse.
  state: analyticsRunState.catch("unknown"),
  sourceRowId: z.string(),
  manifestId: z.string(),
  executedVersionId: z.number().nullable().default(null),
});

export type AnalyticsLastRun = z.infer<typeof analyticsLastRunSchema>;

export const analyticsInvalidOutputSchema = z.object({
  // `.catch("unresolved_outputs")`: an unrecognised cause must not fail the
  // status parse that terminates the publish poll; the fallback still reads as
  // "the outputs could not be resolved", which is what an unknown cause means
  // to the auditor.
  cause: invalidOutputCause.catch("unresolved_outputs"),
  keys: z.array(z.string()).default([]),
});

export type AnalyticsInvalidOutput = z.infer<
  typeof analyticsInvalidOutputSchema
>;

/**
 * The column's derived status, its most recent run, and the link to the
 * published workflow behind it.
 *
 * What this build reads from the backend's `StatusResponse`. The object below
 * is the whole of that claim: a prose list of the same keys would be a second
 * declaration of it, free to drift from the first and silent when it does,
 * because nothing fails on the day the backend carries one more field than the
 * list remembers.
 *
 * What is worth stating is the absence. There is no version pin and no
 * update-available flag — the pin was withdrawn, so no divergence exists for a
 * re-pin banner to read, and nothing here could read one.
 *
 * `errorMessage` is the run's own failure cause and is carried independently of
 * the status: a cancelled run reports `not_run` and may still carry a message,
 * because the status describes the remedy rather than whether there is anything
 * to read.
 *
 * `publishedWorkflowUrl` is AuditBoard's read-only link to the bound workflow's
 * published version. The backend builds it only for a workflow that still
 * exists and has something published, which is what makes it the publish signal
 * `isPublishPollTerminal` watches: null while the auditor is still building,
 * non-null once they have published. `.default(null)` is the safe direction on
 * its own, because a response that somehow omitted the key parses as "not
 * published yet" and the poll keeps waiting rather than reporting a publication
 * that never happened.
 *
 * `workflowName` is the other half of that modal affordance and is sent too. It
 * is declared because the review pane's WORKFLOW section labels its link with
 * it. The rule against declaring it — a declared field nothing reads is how
 * `updateAvailable` came to be trusted — is satisfied by having the reader, not
 * by leaving the field stripped.
 */
export const analyticsStatusSchema = z.object({
  status: workflowStatus,
  // The wording for this column's own status, authored once server-side beside
  // the status enum. The work-paper export is a Python consumer of the same
  // strings, so a copy constant declared in a React component would be
  // unreachable to it and the two would drift into separate wordings of one
  // message. Empty for `ok`, which renders nothing.
  statusCopy: z.string(),
  // The deleted workflow's column-scoped copy, authored server-side beside
  // `statusCopy` and populated only in the `workflow_deleted` state. Null
  // rather than empty elsewhere, so a surface cannot render a deleted state's
  // wording off a column that still has its workflow. The key is on the wire in
  // every state carrying that null, so the question to ask of it is whether it
  // is null and never whether it is there.
  deletedWorkflowCopy: z
    .object({
      review: z.string(),
      stepPreviewTitle: z.string(),
      stepPreviewBody: z.string(),
      stepPreviewHeader: z.string(),
    })
    .nullable()
    .default(null),
  lastRun: analyticsLastRunSchema.nullable().default(null),
  errorMessage: z.string().nullable().default(null),
  invalidOutput: analyticsInvalidOutputSchema.nullable().default(null),
  // AuditBoard's read-only link to the bound workflow's published version, and
  // the workflow's own name. Both are live reads of the workflow resource. The
  // binding Midship stores holds the workflow id; the `workflowName` on the
  // column beside it is the label captured when the column was authored, which
  // an upstream rename leaves behind — so a surface labelling a link to the live
  // workflow reads the name here or names no name at all.
  //
  // The URL is null whenever there is nothing to open — a deleted workflow, an
  // unpublished one, a column naming none, a disabled credential — and a surface
  // renders no link at all rather than a dead one. The name is present whenever
  // AuditBoard reports one, including for a named but unopenable workflow, and
  // is absent rather than empty where AuditBoard reports none, so a caller is
  // never handed a label with a hole in it.
  publishedWorkflowUrl: z.string().nullable().default(null),
  /**
   * The Cascade builder link for a shell built from Midship and not yet
   * attached, recomposed by the backend per read off the binding's `builtAt`
   * provenance. Restores the hand-off's "Open in Optro Analytics" after a
   * reload, where the configure response that first carried it is gone. Null
   * in every other state, including a create-time pick and a published
   * workflow, which gets `publishedWorkflowUrl` above instead. `.default(null)`
   * is the safe direction: an omitted key renders no link rather than a dead
   * one.
   */
  builderUrl: z.string().nullable().default(null),
  workflowName: z.string().nullable().default(null),
  /**
   * Whether AuditBoard would accept an attach of this column's workflow right
   * now: its promoted version's test and review complete, and its
   * effectiveness effective.
   *
   * Not the same as `publishedWorkflowUrl` being non-null. Publication and
   * promotion are necessary but not sufficient — AuditBoard answers 422 "This
   * workflow has not been verified yet" on an attach of a published but
   * unverified workflow.
   *
   * `null` means the backend could not establish it: no workflow, nothing
   * promoted to ask about, or the read failed. Never render `null` as "not
   * verified" — it means unknown, and the poll treats it as keep waiting.
   * `.default(null)` is the safe direction for the same reason.
   */
  isVerified: z.boolean().nullable().default(null),
});

export type AnalyticsStatus = z.infer<typeof analyticsStatusSchema>;

/**
 * One file the run published, as the manifest recorded it.
 *
 * `rowCount` is null for an output with no rows to count — a published chart —
 * and such an output is still registered, marked `unclassified`.
 */
export const analyticsRunOutputSummarySchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  fileFormat: z.string(),
  byteSize: z.number(),
  rowCount: z.number().nullable().default(null),
  ordinal: z.number(),
  // `.catch("unclassified")`: an output with a role this build does not know
  // must not vanish the whole manifest; `unclassified` is the enum's own bucket
  // for exactly that.
  role: analyticsOutputRole.catch("unclassified"),
  description: z.string().nullable().default(null),
});

export type AnalyticsRunOutputSummary = z.infer<
  typeof analyticsRunOutputSummarySchema
>;

/**
 * A record of a judgment, read back — never re-derived.
 *
 * Every classifier field being null is a valid response rather than an error: a
 * manifest is written for every run that reached a terminal state, including one
 * the classifier never saw, and the pane renders that refusal.
 */
export const analyticsRunManifestResponseSchema = z.object({
  runId: z.string(),
  classifiedAt: z.string(),
  classifierModelId: z.string().nullable().default(null),
  classifierPromptVersion: z.string().nullable().default(null),
  populationTotal: z.number().nullable().default(null),
  // Returned with the total, never instead of it: the tab shows where the number
  // came from rather than asserting it. `.catch(null)` covers both an omitted
  // key and an unrecognised source: an unnamed provenance must not fail the
  // whole manifest.
  populationSource: analyticsPopulationSource.nullable().catch(null),
  populationStepId: z.string().nullable().default(null),
  outputs: z.array(analyticsRunOutputSummarySchema).default([]),
  // The deleted workflow's run-scoped copy. Run-scoped and not column-scoped
  // because these sentences name *this* run's date and population: the status
  // read reports the column's most recent run, so copy built there would date
  // an older row's pane by a newer run.
  //
  // Served for every column with a workflow binding, whether or not the
  // workflow still exists, and null only where the column has none. So its
  // presence is not a deletion signal: whether the column is in the deleted
  // state is the status read's answer, and a surface reads this only behind a
  // `workflow_deleted` status. The key is on the wire in every state carrying
  // that null, so the question to ask of it is whether it is null and never
  // whether it is there.
  deletedWorkflowCopy: z
    .object({
      recordedAgainst: z.string(),
      stepsTitle: z.string(),
      stepsBody: z.string(),
      stepsReassurance: z.string(),
    })
    .nullable()
    .default(null),
});

export type AnalyticsRunManifestResponse = z.infer<
  typeof analyticsRunManifestResponseSchema
>;

/**
 * A signed URL for one published output.
 *
 * Its own route rather than a field on the manifest read: the URL grants direct
 * access to the bytes outside this API, so it is minted per request behind a
 * permission the manifest read does not carry, and never handed to a reader who
 * only listed the file.
 */
export const analyticsOutputDownloadUrlSchema = z.object({
  url: z.string(),
});

export type AnalyticsOutputDownloadUrl = z.infer<
  typeof analyticsOutputDownloadUrlSchema
>;

/**
 * One step of the run.
 *
 * `state` stays an open string: it is the workflow engine's own step vocabulary
 * read back out of the stored step log, not the run lifecycle. `completedAt`
 * null is the only thing separating *still running* from *ran and produced
 * nothing*. `rule` is the step definition's own predicate or keys; null means
 * no rule was read, never that the step applies no rule.
 *
 * `stepId` is the step's identity within the run, and the join to its card. It
 * is declared with a default rather than required because not every deployment
 * sends it, and a required field would fail the whole parse against a response
 * that omits it — taking the run's steps down with it rather than one join.
 */
export const analyticsRunStepSchema = z.object({
  stepId: z.string().nullable().default(null),
  name: z.string(),
  operation: z.string(),
  state: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullable().default(null),
  rule: z.string().nullable().default(null),
});

export type AnalyticsRunStep = z.infer<typeof analyticsRunStepSchema>;

export const analyticsRunPhaseSchema = z.object({
  heading: z.string(),
  boundInputFiles: z.array(z.string()).default([]),
  steps: z.array(analyticsRunStepSchema).default([]),
});

export type AnalyticsRunPhase = z.infer<typeof analyticsRunPhaseSchema>;

export const analyticsRunStepsResponseSchema = z.object({
  phases: z.array(analyticsRunPhaseSchema).default([]),
});

export type AnalyticsRunStepsResponse = z.infer<
  typeof analyticsRunStepsResponseSchema
>;

/**
 * One declared file parameter — one feed.
 *
 * Carries no filename and nothing a client could read as one: the auditor is
 * told which slot they are filling and what belongs in it, never which file to
 * go and find.
 */
export const analyticsFileParameterSchema = z.object({
  paramKey: z.string(),
  label: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  required: z.boolean(),
});

export type AnalyticsFileParameter = z.infer<
  typeof analyticsFileParameterSchema
>;

/**
 * A read-time projection of the activated version's file parameters. Nothing is
 * stored, so a parameter added when the workflow's active version changes
 * appears here with no migration. A column with no published workflow returns an
 * empty list.
 *
 * `workflowDeleted` is the backend's own reading of the workflow, not an
 * inference from the empty list: a deleted workflow and one declaring no file
 * inputs both answer `parameters: []`, and only this field tells them apart. It
 * defaults to false so a backend that predates the field still parses.
 */
export const analyticsFileParametersResponseSchema = z.object({
  parameters: z.array(analyticsFileParameterSchema).default([]),
  workflowDeleted: z.boolean().default(false),
});

export type AnalyticsFileParametersResponse = z.infer<
  typeof analyticsFileParametersResponseSchema
>;

/**
 * The column's stored assignments, keyed by parameter key.
 *
 * Eligibility is deliberately absent: whether a document may be used as an
 * analytics input is one state per document per test table, and answering it
 * from these bindings would be right for shared inputs and silently empty for
 * sample-export ones.
 */
export const analyticsInputBindingsResponseSchema = z.object({
  bindings: z.record(InputBindingSchema).default({}),
});

export type AnalyticsInputBindingsResponse = z.infer<
  typeof analyticsInputBindingsResponseSchema
>;

/** Kept out of the column create body: `analyticsColumn` has no category slot. */
export interface PendingWorkflowBuild {
  categoryId: number;
}
